import { resolveAsset } from "@/lib/api";

const PROD_HOSTS = ["servallbikes.in", "www.servallbikes.in"];

const upsertMeta = (name, content, attr = "name") => {
    if (!content) {
        const existing = document.querySelector(`meta[${attr}="${name}"]`);
        if (existing) existing.remove();
        return;
    }
    let el = document.querySelector(`meta[${attr}="${name}"]`);
    if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
    }
    el.setAttribute("content", content);
};

const upsertLink = (rel, href) => {
    if (!href) return;
    let el = document.querySelector(`link[rel="${rel}"]`);
    if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
    }
    el.setAttribute("href", href);
};

const fillTemplate = (template, vars = {}) =>
    (template || "").replace(/\{(\w+)\}/g, (_, key) => vars[key] ?? "");

export const applyPageSeo = ({
    settings = {},
    pages = {},
    pageKey,
    title,
    description,
    path = "/",
    image,
}) => {
    const base = (settings.seo_canonical_base || "https://servallbikes.in").replace(/\/$/, "");
    const canonical = `${base}${path.startsWith("/") ? path : `/${path}`}`;
    const resolvedTitle =
        title ||
        pages[`${pageKey}_seo_title`] ||
        settings.seo_default_title ||
        settings.site_name ||
        "Servall Bikes";
    const resolvedDescription =
        description ||
        pages[`${pageKey}_seo_description`] ||
        settings.seo_default_description ||
        settings.site_tagline ||
        "";

    const ogImage = resolveAsset(image || settings.seo_og_image || "");
    const keywords = settings.seo_keywords || "";

    document.title = resolvedTitle;
    upsertMeta("description", resolvedDescription);
    upsertMeta("keywords", keywords);
    upsertLink("canonical", canonical);
    upsertMeta("og:type", "website", "property");
    upsertMeta("og:title", resolvedTitle, "property");
    upsertMeta("og:description", resolvedDescription, "property");
    upsertMeta("og:url", canonical, "property");
    upsertMeta("og:site_name", settings.site_name || "Servall Bikes", "property");
    if (ogImage) upsertMeta("og:image", ogImage, "property");
    upsertMeta("twitter:card", "summary_large_image");
    upsertMeta("twitter:title", resolvedTitle);
    upsertMeta("twitter:description", resolvedDescription);
    if (ogImage) upsertMeta("twitter:image", ogImage);
};

export const bikeDetailSeo = (bike, pages = {}, settings = {}) => {
    const template =
        pages.bike_detail_seo_title_template ||
        "{name} — On-road Price | Servall Bikes";
    const descTemplate =
        pages.bike_detail_seo_description_template ||
        "Get on-road quote for {name} ({brand}). Compare specs, EMI, and enquire at your nearest Servall outlet.";
    return {
        title: fillTemplate(template, {
            name: bike?.name || "",
            brand: bike?.brand || "",
        }),
        description: fillTemplate(descTemplate, {
            name: bike?.name || "",
            brand: bike?.brand || "",
        }),
        image: bike?.image || "",
    };
};

export const newsDetailSeo = (article, pages = {}, settings = {}) => {
    const template =
        pages.news_detail_seo_title_template || "{title} | Servall Bikes News";
    const descTemplate =
        pages.news_detail_seo_description_template || "{excerpt}";
    return {
        title: fillTemplate(template, {
            title: article?.title || "",
        }),
        description: fillTemplate(descTemplate, {
            excerpt: article?.excerpt || "",
            title: article?.title || "",
        }),
        image: article?.image || "",
    };
};

let trackingInitialized = false;
let trackingGaId = "";
let trackingAdsId = "";

export const isTrackingReady = () => trackingInitialized;

/** SPA page view — call on every client-side route change (including first after init). */
export const trackPageView = (pathname = "/", search = "") => {
    if (!trackingInitialized) return;
    const pagePath = `${pathname || "/"}${search || ""}`;
    const pageLocation = `${window.location.origin}${pagePath}`;

    if (window.gtag) {
        if (trackingGaId) {
            window.gtag("config", trackingGaId, {
                page_path: pagePath,
                page_location: pageLocation,
            });
        }
        if (trackingAdsId && trackingAdsId !== trackingGaId) {
            window.gtag("config", trackingAdsId, {
                page_path: pagePath,
                page_location: pageLocation,
            });
        }
    }
    if (window.fbq) window.fbq("track", "PageView");
};

export const initTracking = (settings = {}) => {
    if (!settings.tracking_enabled || trackingInitialized) return;

    const host = (window.location.hostname || "").toLowerCase();
    if (settings.tracking_production_only !== false && !PROD_HOSTS.includes(host)) return;

    const gaId = (settings.google_analytics_id || "").trim();
    const adsId = (settings.google_ads_id || "").trim();
    const pixelId = (settings.meta_pixel_id || "").trim();
    const verification = (settings.google_site_verification || "").trim();

    if (verification) upsertMeta("google-site-verification", verification);

    if (gaId || adsId) {
        window.dataLayer = window.dataLayer || [];
        function gtag() {
            window.dataLayer.push(arguments);
        }
        window.gtag = gtag;
        gtag("js", new Date());
        // Disable auto page_view — SiteTracking fires trackPageView on route changes
        if (gaId) gtag("config", gaId, { send_page_view: false });
        if (adsId && adsId !== gaId) gtag("config", adsId, { send_page_view: false });
        trackingGaId = gaId;
        trackingAdsId = adsId;

        const loaderId = adsId || gaId;
        if (!document.querySelector('script[data-servall-gtag="1"]')) {
            const s = document.createElement("script");
            s.async = true;
            s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(loaderId)}`;
            s.setAttribute("data-servall-gtag", "1");
            document.head.appendChild(s);
        }
    }

    if (pixelId && !window.fbq) {
        !(function (f, b, e, v, n, t, s) {
            if (f.fbq) return;
            n = f.fbq = function () {
                n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
            };
            if (!f._fbq) f._fbq = n;
            n.push = n;
            n.loaded = true;
            n.version = "2.0";
            n.queue = [];
            t = b.createElement(e);
            t.async = true;
            t.src = v;
            t.setAttribute("data-servall-pixel", "1");
            s = b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t, s);
        })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
        window.fbq("init", pixelId);
        // PageView fired via trackPageView after init
    }

    trackingInitialized = true;
};

export const trackLeadConversion = (settings = {}) => {
    if (window.gtag) {
        window.gtag("event", "generate_lead", { event_category: "engagement" });
        const conversionId = (settings.google_ads_conversion_label || settings.google_ads_id || "").trim();
        if (conversionId) {
            window.gtag("event", "conversion", { send_to: conversionId });
        }
    }
    if (window.fbq) window.fbq("track", "Lead");
};
